import type { Section, SectionType } from '@/lib/types'

export type ObjectiveSectionType = Extract<SectionType, 'Listening' | 'Reading'>

/** Listening and Reading are the only sections scored as objective points. */
export function isObjectiveSectionType(value: unknown): value is ObjectiveSectionType {
  return value === 'Listening' || value === 'Reading'
}

// Stored grader shares and authored capacities are rounded to four decimal
// places. Integer ten-thousandths keep aggregation exact until presentation.
export const OBJECTIVE_POINT_SCALE = 10_000

function authoredPointUnits(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return null
  const units = Math.round(value * OBJECTIVE_POINT_SCALE)
  return units > 0 ? units : null
}

/**
 * Returns authored Listening/Reading capacity, or `null` when the nested
 * objective definition is absent/incomplete. Writing and Speaking criteria
 * scores are deliberately outside this measure.
 */
export function authoredObjectivePointCapacity(
  sections: readonly Section[] | null | undefined,
): number | null {
  if (!Array.isArray(sections)) return null

  let foundObjectiveSection = false
  let foundObjectiveQuestion = false
  let pointUnits = 0

  for (const section of sections) {
    if (!isObjectiveSectionType(section?.type)) continue
    foundObjectiveSection = true
    let sectionHasObjectiveQuestion = false

    if (!Array.isArray(section.parts)) return null
    for (const part of section.parts) {
      if (!Array.isArray(part?.question_groups)) return null
      for (const group of part.question_groups) {
        if (!Array.isArray(group?.questions)) return null
        for (const question of group.questions) {
          sectionHasObjectiveQuestion = true
          foundObjectiveQuestion = true
          const units = authoredPointUnits(question?.points_value)
          if (units === null) return null
          pointUnits += units
        }
      }
    }
    if (!sectionHasObjectiveQuestion) return null
  }

  if (!foundObjectiveSection || !foundObjectiveQuestion) return null
  return pointUnits / OBJECTIVE_POINT_SCALE
}

const POINT_NUMBER = new Intl.NumberFormat('en-GB', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

/** Locale-stable display rounding with no misleading trailing zeroes. */
export function formatPointCount(points: number): string {
  if (!Number.isFinite(points)) return '—'
  return POINT_NUMBER.format(Object.is(points, -0) ? 0 : points)
}

export function pointUnit(points: number): 'point' | 'points' {
  // Match the two-decimal presentation: 3 × 0.3333 is shown as "1 point".
  return Math.round(points * 100) / 100 === 1 ? 'point' : 'points'
}
